import * as fabric from 'fabric';

// ── 정다각형 꼭짓점 생성 (오각형, 육각형 등) ──
const createRegularPolygonPoints = (size, sides) => {
  const r = size / 2;
  const pts = [];
  for (let i = 0; i < sides; i++) {
    const angle = (i * 2 * Math.PI) / sides - Math.PI / 2;
    pts.push({ x: r + r * Math.cos(angle), y: r + r * Math.sin(angle) });
  }
  return pts;
};

// ── 별 꼭짓점 (5각 별) ──
const createStarPoints = (outerR, innerR, points = 5) => {
  const result = [];
  const step = Math.PI / points;
  for (let i = 0; i < 2 * points; i++) {
    const r = i % 2 === 0 ? outerR : innerR;
    const angle = i * step - Math.PI / 2;
    result.push({ x: outerR + r * Math.cos(angle), y: outerR + r * Math.sin(angle) });
  }
  return result;
};

// ── 마름모 꼭짓점 ──
const createDiamondPoints = (size) => [
  { x: size / 2, y: 0 },
  { x: size, y: size / 2 },
  { x: size / 2, y: size },
  { x: 0, y: size / 2 },
];

// ── 블록 화살표 꼭짓점 ──
const createArrowPoints = (w, h) => {
  const shaftW = h * 0.35;
  const headStart = w * 0.55;
  const cy = h / 2;
  return [
    { x: 0, y: cy - shaftW / 2 },
    { x: headStart, y: cy - shaftW / 2 },
    { x: headStart, y: 0 },
    { x: w, y: cy },
    { x: headStart, y: h },
    { x: headStart, y: cy + shaftW / 2 },
    { x: 0, y: cy + shaftW / 2 },
  ];
};

// ── 십자가 꼭짓점 ──
const createCrossPoints = (size) => {
  const t = size / 3;
  return [
    { x: t, y: 0 }, { x: 2 * t, y: 0 },
    { x: 2 * t, y: t }, { x: size, y: t },
    { x: size, y: 2 * t }, { x: 2 * t, y: 2 * t },
    { x: 2 * t, y: size }, { x: t, y: size },
    { x: t, y: 2 * t }, { x: 0, y: 2 * t },
    { x: 0, y: t }, { x: t, y: t },
  ];
};

// ── 하트 SVG 패스 ──
const createHeartPath = (size) => {
  const s = size / 100;
  return `M ${50 * s} ${90 * s} C ${50 * s} ${90 * s} ${10 * s} ${60 * s} ${10 * s} ${35 * s} C ${10 * s} ${15 * s} ${30 * s} ${5 * s} ${50 * s} ${25 * s} C ${70 * s} ${5 * s} ${90 * s} ${15 * s} ${90 * s} ${35 * s} C ${90 * s} ${60 * s} ${50 * s} ${90 * s} ${50 * s} ${90 * s} Z`;
};

/**
 * 도형 팩토리: type → fabric 객체 생성
 * @param {string} type - 도형 타입
 * @param {object} common - 공통 속성 (stroke, strokeWidth, opacity 등)
 * @param {object} params - { cx, cy, size, fabricFill, fabricStroke, rx, ry }
 * @returns {fabric.Object|null}
 */
export const createShapeObject = (type, common, { cx, cy, size, fabricFill, fabricStroke, rx, ry }) => {
  const ts = Date.now();

  const creators = {
    rect: () => new fabric.Rect({
      ...common, left: cx, top: cy, width: size, height: size,
      fill: fabricFill, rx: rx || 0, ry: ry || 0,
      id: `rect-${ts}`, name: 'Rectangle',
    }),
    roundedRect: () => new fabric.Rect({
      ...common, left: cx, top: cy, width: size, height: size,
      fill: fabricFill, rx: 20, ry: 20,
      id: `rect-${ts}`, name: 'RoundedRect',
    }),
    circle: () => new fabric.Ellipse({
      ...common, left: cx, top: cy, rx: size / 2, ry: size / 2,
      fill: fabricFill, id: `circle-${ts}`, name: 'Circle',
    }),
    triangle: () => new fabric.Triangle({
      ...common, left: cx, top: cy, width: size, height: size,
      fill: fabricFill, id: `triangle-${ts}`, name: 'Triangle',
    }),
    star: () => new fabric.Polygon(createStarPoints(size / 2, size / 5), {
      ...common, left: cx, top: cy, fill: fabricFill,
      id: `star-${ts}`, name: 'Star',
    }),
    diamond: () => new fabric.Polygon(createDiamondPoints(size), {
      ...common, left: cx, top: cy, fill: fabricFill,
      id: `diamond-${ts}`, name: 'Diamond',
    }),
    pentagon: () => new fabric.Polygon(createRegularPolygonPoints(size, 5), {
      ...common, left: cx, top: cy, fill: fabricFill,
      id: `pentagon-${ts}`, name: 'Pentagon',
    }),
    hexagon: () => new fabric.Polygon(createRegularPolygonPoints(size, 6), {
      ...common, left: cx, top: cy, fill: fabricFill,
      id: `hexagon-${ts}`, name: 'Hexagon',
    }),
    arrow: () => new fabric.Polygon(createArrowPoints(size * 1.5, size), {
      ...common, left: cx - size * 0.25, top: cy, fill: fabricFill,
      id: `arrow-${ts}`, name: 'Arrow',
    }),
    cross: () => new fabric.Polygon(createCrossPoints(size), {
      ...common, left: cx, top: cy, fill: fabricFill,
      id: `cross-${ts}`, name: 'Cross',
    }),
    heart: () => new fabric.Path(createHeartPath(size), {
      ...common, left: cx, top: cy, fill: fabricFill,
      id: `heart-${ts}`, name: 'Heart',
    }),
    line: () => new fabric.Line([cx, cy + size / 2, cx + size, cy + size / 2], {
      ...common, stroke: fabricStroke || fabricFill, fill: null,
      id: `line-${ts}`, name: 'Line',
    }),
  };

  const creator = creators[type] || creators.rect;
  return creator();
};
